import { z } from "zod";
z.config({ jitless: true });
export const adminOperationsSchemaVersionSchema = z.literal("2026-09-23.1");
export const adminOperationsExtendedSchemaVersionSchema = z.literal("2026-09-23.2");
export const adminOperationsPartnerSchemaVersionSchema = z.literal("2026-09-24.1");
export const adminOperationsPilotSchemaVersionSchema = z.literal("2026-09-24.2");
export const adminOperationsControlSchemaVersionSchema = z.literal("2026-09-24.3");
export const adminOperationsAnySchemaVersionSchema = z.union([
    adminOperationsSchemaVersionSchema,
    adminOperationsExtendedSchemaVersionSchema,
    adminOperationsPartnerSchemaVersionSchema,
    adminOperationsPilotSchemaVersionSchema,
    adminOperationsControlSchemaVersionSchema,
]);
export const adminAuthorizationModelVersionSchema = z.literal("2026-09-23.3");
export const adminStageSchema = z.enum(["development", "staging", "public-sandbox"]);
/**
 * Human operator personas. ADMIN is held only by the founder and the owners named on the reviewed
 * owner list each release carries; only the founder grants or removes access. Break-glass is an
 * expiring activation, never a standing role.
 */
export const adminRoleSchema = z.enum(["ADMIN", "BUSINESS_PARTNER", "SUPPORT_AGENT"]);
export const adminPermissionSchema = z.enum([
    "SESSION_READ",
    "HEALTH_READ",
    "BUSINESS_METRICS_READ",
    "OPERATOR_ACCESS_READ",
    "OPERATOR_ACCESS_REQUEST",
    "OPERATOR_ACCESS_APPROVE",
    "CASE_CREATE",
    "CASE_LIST",
    "CASE_READ",
    "CASE_RESOLVE",
    "EVIDENCE_SEARCH",
    "CUSTOMER_RESOLVE",
    "CUSTOMER_TIMELINE_READ",
    "SUPPORT_GRANT_REQUEST",
    "SUPPORT_DIAGNOSTICS_READ",
    "SUPPORT_TICKET_LIST",
    "SUPPORT_TICKET_MANAGE",
    // Ticket-bound courtesy issuance remains separate from commercial batch creation.
    "SUPPORT_REMEDY_ISSUE",
    "GIFT_READ",
    "GIFT_BATCH_CREATE",
    "GIFT_EXPORT_ISSUE",
    "GIFT_EXPORT_RECOVER",
    "REDEMPTION_READ",
    "ORDER_READ",
    "PAYMENT_READ",
    "FULFILLMENT_READ",
    "FULFILLMENT_COMMAND",
    "RECONCILIATION_PREVIEW",
    "RECONCILIATION_EXECUTE",
    "AUDIT_READ",
    "AUDIT_INTEGRITY_READ",
    // The pilot cockpit (2026-10-16): what needs a person, a book's journey, each customer.
    "OBSERVABILITY_READ",
    // Totals only, never a customer or a book: AI operations and Reliability, which business
    // partners see too (owner decision, 2026-10-16; review, 2026-10-18).
    "OBSERVABILITY_AGGREGATE_READ",
    "JOURNEY_READ",
    "CUSTOMER_360_READ",
]);
export const adminRolePermissions = {
    ADMIN: adminPermissionSchema.options,
    BUSINESS_PARTNER: [
        "SESSION_READ",
        "HEALTH_READ",
        "BUSINESS_METRICS_READ",
        "OBSERVABILITY_AGGREGATE_READ",
    ],
    SUPPORT_AGENT: [
        "SESSION_READ",
        "HEALTH_READ",
        "CASE_CREATE",
        "CASE_LIST",
        "CASE_READ",
        "CASE_RESOLVE",
        "CUSTOMER_RESOLVE",
        "CUSTOMER_TIMELINE_READ",
        "SUPPORT_GRANT_REQUEST",
        "SUPPORT_DIAGNOSTICS_READ",
        "SUPPORT_TICKET_LIST",
        "SUPPORT_TICKET_MANAGE",
        "SUPPORT_REMEDY_ISSUE",
        "REDEMPTION_READ",
        "ORDER_READ",
        "PAYMENT_READ",
        // Read-only printing status: support is emailed about print orders that need the team
        // (owner decision 2, 2026-10-15) and must be able to open them.
        "FULFILLMENT_READ",
    ],
};
export function adminPermissionsForRoles(roles) {
    return [...new Set(roles.flatMap((role) => adminRolePermissions[role]))].sort();
}
/**
 * Reviewable route-level authorization envelope. Routes with more than one permission are
 * discriminated by the validated request or stored command type; they never imply that either
 * permission grants the other domain's action.
 */
export const adminRoutePermissionRequirements = {
    "GET /api/admin/v1/session": ["SESSION_READ"],
    "GET /api/admin/v1/health": ["HEALTH_READ"],
    "POST /api/admin/v1/business/overview": ["BUSINESS_METRICS_READ"],
    "POST /api/admin/v1/business/qvc-scenario": ["BUSINESS_METRICS_READ"],
    "POST /api/admin/v1/activity/overview": ["BUSINESS_METRICS_READ"],
    "POST /api/admin/v1/business-partners/invitations": ["OPERATOR_ACCESS_REQUEST"],
    "POST /api/admin/v1/business-partners/{operatorId}/disable": ["OPERATOR_ACCESS_REQUEST"],
    "POST /api/admin/v1/owners/{operatorId}/grant": ["OPERATOR_ACCESS_REQUEST"],
    "POST /api/admin/v1/owners/{operatorId}/disable": ["OPERATOR_ACCESS_REQUEST"],
    "POST /api/admin/v1/access-requests": ["OPERATOR_ACCESS_REQUEST"],
    "POST /api/admin/v1/team/internal-name": ["OPERATOR_ACCESS_REQUEST"],
    "POST /api/admin/v1/access-requests/{requestId}/approve": ["OPERATOR_ACCESS_APPROVE"],
    "POST /api/admin/v1/break-glass-activations": ["OPERATOR_ACCESS_APPROVE"],
    "POST /api/admin/v1/break-glass-activations/{activationId}/approve": ["OPERATOR_ACCESS_APPROVE"],
    // What an owner's access change came to: applied, failed or still waiting (stage 2, 2026-11-02).
    "GET /api/admin/v1/access-requests/{requestId}": ["OPERATOR_ACCESS_REQUEST"],
    // Everything waiting on a second owner: protected actions, access requests and break-glass
    // requests (stage 2, 2026-11-02).
    "GET /api/admin/v1/approvals/pending": ["OPERATOR_ACCESS_APPROVE"],
    "GET /api/admin/v1/access-review": ["OPERATOR_ACCESS_READ"],
    "POST /api/admin/v1/cases": ["CASE_CREATE"],
    "GET /api/admin/v1/cases": ["CASE_LIST"],
    "GET /api/admin/v1/cases/{caseId}": ["CASE_READ"],
    "POST /api/admin/v1/cases/{caseId}/resolve": ["CASE_RESOLVE"],
    "POST /api/admin/v1/evidence/search": ["EVIDENCE_SEARCH"],
    "POST /api/admin/v1/customers/resolve": ["CUSTOMER_RESOLVE"],
    "POST /api/admin/v1/customers/timeline": ["CUSTOMER_TIMELINE_READ"],
    "POST /api/admin/v1/investigations/return-link": ["CUSTOMER_TIMELINE_READ"],
    "POST /api/admin/v1/investigations/resume": ["CUSTOMER_TIMELINE_READ"],
    "POST /api/admin/v1/support/tickets/detail": ["SUPPORT_DIAGNOSTICS_READ"],
    "POST /api/admin/v1/support/tickets/search": ["SUPPORT_TICKET_LIST"],
    // A support request's book: its edition summary, and a five-minute link to one exact file
    // (recent MFA) (I3, 2026-10-28).
    "POST /api/admin/v1/support/tickets/edition-summary": ["SUPPORT_TICKET_MANAGE"],
    "POST /api/admin/v1/support/tickets/edition-file": ["SUPPORT_TICKET_MANAGE"],
    "POST /api/admin/v1/support/tickets/{ticketId}/transition": [
        "SUPPORT_TICKET_MANAGE",
        "SUPPORT_REMEDY_ISSUE",
    ],
    // Who a shared request can be handed to, by non-identifying label (I3.2, 2026-10-28).
    "POST /api/admin/v1/support/teammates": ["SUPPORT_TICKET_MANAGE"],
    "POST /api/admin/v1/commerce/investigate": [
        "GIFT_READ",
        "REDEMPTION_READ",
        "ORDER_READ",
        "PAYMENT_READ",
        "FULFILLMENT_READ",
    ],
    "POST /api/admin/v1/support-grants": ["SUPPORT_GRANT_REQUEST"],
    "POST /api/admin/v1/support-grants/status": ["SUPPORT_GRANT_REQUEST"],
    "POST /api/admin/v1/support-grants/diagnostics": ["SUPPORT_DIAGNOSTICS_READ"],
    "POST /api/admin/v1/gift-code-batches/preview": ["GIFT_BATCH_CREATE"],
    "POST /api/admin/v1/gift-code-batches/search": ["GIFT_READ"],
    "POST /api/admin/v1/gift-code-batches/all": ["GIFT_READ"],
    "POST /api/admin/v1/gift-code-batches": ["GIFT_BATCH_CREATE"],
    "GET /api/admin/v1/gift-code-batches/{batchId}": ["GIFT_READ"],
    "POST /api/admin/v1/gift-code-batches/{batchId}/export-leases": ["GIFT_EXPORT_ISSUE"],
    // Downloading a batch's codes again while they are still held (2026-10-21).
    "POST /api/admin/v1/gift-code-batches/{batchId}/export-recoveries": ["GIFT_EXPORT_RECOVER"],
    // A run's codes were handed over to its partner or store (redesign stage 1, 2026-11-02).
    "POST /api/admin/v1/gift-code-batches/{batchId}/handed-over": ["GIFT_EXPORT_ISSUE"],
    // A goodwill voucher for a customer whose book went wrong (2026-10-21).
    "POST /api/admin/v1/vouchers/goodwill": ["GIFT_BATCH_CREATE"],
    "POST /api/admin/v1/vouchers/issue-email": ["GIFT_BATCH_CREATE"],
    "POST /api/admin/v1/fulfillment/summary": ["FULFILLMENT_READ"],
    "POST /api/admin/v1/fulfillment/orders": ["FULFILLMENT_READ"],
    // Owners only: a print order's staff alert counts (I3, 2026-10-28).
    "POST /api/admin/v1/fulfillment/alert-history": ["FULFILLMENT_READ"],
    "POST /api/admin/v1/fulfillment/customer-messages": ["FULFILLMENT_READ"],
    "POST /api/admin/v1/books/overview": ["CUSTOMER_TIMELINE_READ"],
    "POST /api/admin/v1/books/totals": ["BUSINESS_METRICS_READ"],
    // Owners only: one book's edition summary for the Books and Orders pages (I3, 2026-10-28).
    "POST /api/admin/v1/books/edition-summary": ["FULFILLMENT_READ"],
    "POST /api/admin/v1/actions/preview": ["RECONCILIATION_PREVIEW", "FULFILLMENT_READ"],
    "POST /api/admin/v1/actions/{previewId}/approve": [
        "RECONCILIATION_EXECUTE",
        "FULFILLMENT_COMMAND",
    ],
    "POST /api/admin/v1/actions/{previewId}/confirm": [
        "RECONCILIATION_EXECUTE",
        "FULFILLMENT_COMMAND",
    ],
    "GET /api/admin/v1/commands/{commandId}": ["RECONCILIATION_PREVIEW", "FULFILLMENT_READ"],
    // A second owner reads what they are asked to approve; erasure progress (2026-10-21).
    "GET /api/admin/v1/actions/{previewId}": ["RECONCILIATION_EXECUTE"],
    "POST /api/admin/v1/actions/erasure-status": ["RECONCILIATION_PREVIEW"],
    "POST /api/admin/v1/audit/search": ["AUDIT_READ"],
    "GET /api/admin/v1/audit/archive-integrity": ["AUDIT_INTEGRITY_READ"],
    "POST /api/admin/v1/observability/attention": ["OBSERVABILITY_READ"],
    "POST /api/admin/v1/observability/books/journey": ["JOURNEY_READ"],
    "POST /api/admin/v1/observability/customers/list": ["CUSTOMER_360_READ"],
    "POST /api/admin/v1/observability/customers/detail": ["CUSTOMER_360_READ"],
    // Owners find a customer by the start of their name (stage 2, 2026-11-02).
    "POST /api/admin/v1/observability/customers/search": ["CUSTOMER_360_READ"],
    "POST /api/admin/v1/observability/ai": ["OBSERVABILITY_AGGREGATE_READ"],
    "POST /api/admin/v1/observability/reliability": ["OBSERVABILITY_AGGREGATE_READ"],
};
export const adminUlidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/u);
export const adminSafeIdentifierSchema = z
    .string()
    .trim()
    .min(1)
    .max(255)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:#/-]*$/u);
export const adminReasonCodeSchema = z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/u);
export const adminOperatorIdSchema = z.string().trim().min(1).max(128);
export const adminSha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
/**
 * An operator's place on the owner list: the founder, a granted owner, a listed owner the founder
 * has not granted ADMIN yet, or neither.
 */
export const adminOwnerAccessSchema = z.enum(["FOUNDER", "OWNER", "LISTED", "NONE"]);
export const adminSessionResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsSchemaVersionSchema,
        authorizationModelVersion: adminAuthorizationModelVersionSchema,
        stage: adminStageSchema,
        operator: z
            .object({
            id: z.string().min(1).max(128),
            roles: z.array(adminRoleSchema).min(1).max(adminRoleSchema.options.length),
            permissions: z
                .array(adminPermissionSchema)
                .min(1)
                .max(adminPermissionSchema.options.length),
        })
            .strict(),
        authenticatedAt: z.string().datetime(),
        expiresAt: z.string().datetime(),
        ownerAccess: adminOwnerAccessSchema.optional(),
    })
        .strict(),
})
    .strict();
export const adminHealthResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsSchemaVersionSchema,
        stage: adminStageSchema,
        state: z.enum(["HEALTHY", "DEGRADED", "ATTENTION_REQUIRED"]),
        checkedAt: z.string().datetime(),
        detailLevel: z.enum(["SUMMARY", "FULL"]),
        alarms: z
            .array(z
            .object({
            name: z.string().min(1).max(255),
            state: z.enum(["OK", "ALARM", "INSUFFICIENT_DATA", "UNKNOWN"]),
        })
            .strict())
            .max(50),
        auditArchive: z
            .object({
            pendingMessages: z.number().int().nonnegative(),
            inFlightMessages: z.number().int().nonnegative(),
            failedMessages: z.number().int().nonnegative(),
        })
            .strict(),
        fulfillmentOperations: z
            .object({
            readiness: z.enum([
                "DARK",
                "PREPARATION_ACTIVE",
                "ONE_ORDER_ACTIVE",
                "BEDTIME_END_TO_END_ACTIVE",
                "CUSTOMER_PRINTING_OPEN",
                "CUSTOMER_PRINTING_DRAINING",
                "CONFIGURATION_MISMATCH",
                "UNKNOWN",
            ]),
            deployment: z
                .object({
                runtime: z.boolean(),
                package: z.boolean(),
                quote: z.boolean(),
                submission: z.boolean(),
                reconciliation: z.boolean(),
                notifications: z.boolean(),
                customerUx: z.boolean(),
                adminActions: z.boolean(),
            })
                .strict(),
            runtimeControl: z
                .object({
                state: z.enum(["DISABLED", "SANDBOX_ONLY", "PRODUCTION_APPROVED", "ABSENT"]),
                environment: z.enum(["SANDBOX", "PRODUCTION"]).optional(),
                phase: z
                    .enum([
                    "PREPARE_ONE_BOOK",
                    "SUBMIT_ONE_ORDER",
                    "BEDTIME_END_TO_END",
                    "CUSTOMER_PRINTING",
                    "CUSTOMER_PRINTING_DRAINING",
                ])
                    .optional(),
                expiresAt: z.string().datetime({ offset: true }).optional(),
                openedAt: z.string().datetime({ offset: true }).optional(),
            })
                .strict(),
        })
            .strict()
            .optional(),
        services: z
            .array(z
            .object({
            id: z
                .string()
                .regex(/^[A-Za-z0-9][A-Za-z0-9._:#/-]*$/u)
                .max(255),
            label: z.string().min(1).max(80),
            state: z.enum(["HEALTHY", "DEGRADED", "ATTENTION_REQUIRED", "UNKNOWN"]),
            summary: z.string().min(1).max(160),
        })
            .strict())
            .max(20)
            .default([]),
        needsAttention: z
            .array(z
            .object({
            code: z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/u),
            label: z.string().min(1).max(120),
            severity: z.enum(["WARNING", "CRITICAL"]),
        })
            .strict())
            .max(20)
            .default([]),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=admin-operations-core.js.map